import { genericMemo } from "@redotech/react-util/component";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import {
  StringMatchOperator,
  stringMatchOperatorToLabel,
  TextMatchExpression,
  textMatchOperators,
} from "@redotech/redo-flows-core-schema-common/expressions/text-expression";
import { Schema } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import {
  BadgeRedoListItem,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { filterTruthy } from "@redotech/util/array";
import { DeepPartial, DeepPartialExcept } from "@redotech/util/type";
import { useEffect, useMemo, useState } from "react";
import { useDelayedAutoFocus } from "../common/use-delayed-auto-focus";

const textMatchOperatorItems: RedoListItem<StringMatchOperator>[] =
  textMatchOperators.map((operator) => ({
    id: operator,
    type: "text",
    text: stringMatchOperatorToLabel[operator],
    value: operator,
  }));

export const TextInspector = genericMemo(function TextInspector({
  schema,
  selectedField,
  expression,
  setExpression,
  showValidationErrors,
}: {
  schema: Schema;
  selectedField: Schema["fields"][string];
  expression: DeepPartialExcept<TextMatchExpression, "field">;
  setExpression: (expression: DeepPartial<TextMatchExpression>) => void;
  showValidationErrors?: boolean;
}) {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  useEffect(() => {
    if (!expression.type || !expression.operator) {
      setExpression({
        ...expression,
        type: "text_match",
        operator: expression.operator ?? "equals",
      });
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expression]);

  const selectedItem = textMatchOperatorItems.find(
    (item) => item.id === expression.operator,
  );

  const [tagInputValue, setTagInputValue] = useState<string>("");
  const [touched, setTouched] = useState(showValidationErrors || false);

  const tags = useMemo(() => {
    return filterTruthy(
      expression.matchValues?.map((value) =>
        value ? { text: value } : undefined,
      ) ?? [],
    );
  }, [expression.matchValues]);

  const delayedAutoFocus = useDelayedAutoFocus(!tags.length);

  const errorText = useMemo(() => {
    if (
      (!expression.matchValues || expression.matchValues.length === 0) &&
      touched
    ) {
      return tagInputValue
        ? t("Press enter to add the value")
        : t("At least one value is required");
    }
    return undefined;
  }, [expression.matchValues, touched, tagInputValue, t]);

  const suggestionType =
    "suggestionType" in selectedField
      ? selectedField.suggestionType
      : undefined;

  const suggestionsLoad = useLoad(async () => {
    if (!suggestionType) {
      return undefined;
    }
    // FIXME: Find a more elegant way to structure this.
    // If we have to add a branch in this useLoad callback for every field we want suggestions for,
    // it could get out of hand quickly.
    switch (suggestionType) {
      case "orderSource":
        return await rpcClient.getOrderSourceNames({ teamId: team._id });
      default:
        return undefined;
    }
  }, [suggestionType, team._id, rpcClient]);

  const suggestionOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    if (!suggestionsLoad.value) {
      return [];
    }
    return suggestionsLoad.value.map((value) => ({
      id: value,
      value,
      type: "badge",
      badge: { text: value },
    }));
  }, [suggestionsLoad.value]);

  const selectedOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    if (!expression.matchValues) {
      return [];
    }
    return expression.matchValues
      .filter((v): v is string => v !== undefined)
      .map((value) => ({
        id: value,
        value,
        type: "badge",
        badge: { text: value },
      }));
  }, [expression.matchValues]);

  const hasSuggestions = suggestionType !== undefined;

  return (
    <>
      <RedoSingleSelectDropdownInput<StringMatchOperator>
        label={t("Text match type")}
        options={textMatchOperatorItems}
        optionSelected={({ item: operator }) => {
          setExpression({ ...expression, operator: operator.value });
        }}
        selectedItem={selectedItem}
        size="xs"
      />

      {hasSuggestions ? (
        <RedoTagsDropdownInput<string>
          autoFocus={delayedAutoFocus}
          description={errorText}
          key={delayedAutoFocus.toString()}
          label={t("Values")}
          loading={suggestionsLoad.pending}
          onBlur={() => setTouched(true)}
          options={suggestionOptions}
          searchString={tagInputValue}
          searchSubmitted={(value) => {
            value = value.trim();
            if (value) {
              setExpression({
                ...expression,
                matchValues: [
                  ...(expression.matchValues ?? []).filter(
                    (v): v is string => v !== undefined,
                  ),
                  value,
                ],
              });
              setTagInputValue("");
            }
          }}
          selectedOptions={selectedOptions}
          setSearchString={setTagInputValue}
          setSelectedOptions={(selected) => {
            setExpression({
              ...expression,
              matchValues: selected.map((option) => option.value),
            });
          }}
          size="xs"
          state={errorText ? "error" : undefined}
        />
      ) : (
        <RedoTagInputField
          autoFocus={delayedAutoFocus}
          description={errorText}
          fitContent
          inputValue={tagInputValue}
          key={delayedAutoFocus.toString()}
          label={t("Values")}
          onBlur={() => setTouched(true)}
          setInputValue={setTagInputValue}
          setTags={(tags) =>
            setExpression({ ...expression, matchValues: tags })
          }
          state={errorText ? "error" : "default"}
          tags={tags}
        />
      )}
    </>
  );
});
